/**
 * §12.3 廠區管理 + §12.3.2 建立精靈。
 *
 * §12.3.1 的核心規則：**鎖住的欄位要顯示「為什麼鎖住」與「鎖住的依據數字」**，
 * 不要只給一個灰掉的輸入框。管理者看到「已發出 2,000 個號碼」就知道
 * 這不是系統在刁難他，而是真的改不得。
 */

import { useState } from 'react'
import { ApiError, api } from '../../api/client.js'
import { useQuery } from '../../api/hooks.js'
import { Alert, Button, Card, Empty, Field, KV, Mono, Pill, Select, Table, Td, TextInput } from '../../components/ui.js'
import { useAuth } from '../../auth/AuthContext.js'
import { count } from '../../../../shared/lib/format.js'
import { navigate } from '../../router.js'

interface Site {
  site_id: string
  panel_id_prefix: string
  item_id_prefix: string
  name: string
  short_name: string
  sort_order: number
  address: string | null
  contact_name: string | null
  tax_id: string | null
  epa_facility_no: string | null
  permit_no: string | null
  permit_valid_until: string | null
  timezone: string
  status: 'setup' | 'active' | 'suspended'
  activated_at: string | null
  usage: {
    eventCount: number; panelIdsIssued: number; itemIdsIssued: number
    assignedUsers: number; openWorkOrders: number
  }
  permitWarning: string | null
}

interface Readiness {
  siteId: string
  passed: number
  total: number
  canActivate: boolean
  items: { no: number; key: string; label: string; ok: boolean; detail: string; href?: string; unavailable?: string }[]
}

const STATUS_LABEL = { setup: '設定中', active: '正常營運', suspended: '暫停營運' } as const

export function AdminSites({ newSite }: { newSite?: boolean }) {
  const { can } = useAuth()
  const sites = useQuery<{ items: Site[] }>('/api/admin/sites')
  const [selected, setSelected] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null)

  if (newSite) {
    return <NewSiteWizard onDone={(id) => { navigate('/admin/sites'); setSelected(id) }} />
  }

  const items = sites.data?.items ?? []

  return (
    <div className="space-y-4">
      {notice && <Alert tone={notice.tone === 'ok' ? 'ok' : 'danger'}>{notice.text}</Alert>}

      <Card
        title="廠區管理"
        subtitle="廠區是主檔，不是部署參數——在這裡新增與編輯，不寫死在遷移檔或環境變數"
        right={can('site.write')
          ? <Button size="sm" variant="primary" onClick={() => navigate('/admin/sites/new')}>建立廠區</Button>
          : undefined}
      >
        {sites.error && <Alert tone="danger" title="讀不到廠區">{sites.error.message}</Alert>}
        {items.length === 0 && !sites.loading && (
          <Empty>系統裡還沒有任何廠區。{can('site.write') && '請先建立第一個。'}</Empty>
        )}
        {items.length > 0 && (
          <Table head={['代碼', '名稱', '狀態', '事件數', '已指派帳號', '']}>
            {items.map((s) => (
              <tr key={s.site_id} className="hover:bg-ink-50">
                <Td><Mono>{s.site_id}</Mono></Td>
                <Td>{s.name}<span className="ml-1 text-xs text-ink-400">{s.short_name}</span></Td>
                <Td>
                  <Pill tone={s.status === 'active' ? 'emerald' : s.status === 'setup' ? 'amber' : 'ink'}>
                    {STATUS_LABEL[s.status]}
                  </Pill>
                  {s.permitWarning && <Pill tone="amber" title={s.permitWarning}>許可證</Pill>}
                </Td>
                <Td className="tabular-nums text-xs">{count(s.usage.eventCount)}</Td>
                <Td className="tabular-nums text-xs">{count(s.usage.assignedUsers)}</Td>
                <Td>
                  <Button size="sm" onClick={() => setSelected(s.site_id === selected ? null : s.site_id)}>
                    {s.site_id === selected ? '收起' : '詳細'}
                  </Button>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {selected && (
        <SiteDetail
          siteId={selected}
          onChanged={() => { sites.reload() }}
          onNotice={setNotice}
        />
      )}
    </div>
  )
}

function SiteDetail({ siteId, onChanged, onNotice }: {
  siteId: string
  onChanged: () => void
  onNotice: (n: { tone: 'ok' | 'danger'; text: string }) => void
}) {
  const { can } = useAuth()
  const detail = useQuery<{
    site: Site
    usage: Site['usage']
    fieldLocks: { field: string; locked: boolean; reason?: string }[]
    permitWarning: string | null
    canDelete: boolean
  }>(`/api/admin/sites/${siteId}`)
  const readiness = useQuery<Readiness>(`/api/admin/sites/${siteId}/readiness`)

  const act = async (fn: () => Promise<void>, okText: string) => {
    try {
      await fn()
      onNotice({ tone: 'ok', text: okText })
      detail.reload(); readiness.reload(); onChanged()
    } catch (e) {
      onNotice({ tone: 'danger', text: e instanceof ApiError ? e.message : String(e) })
    }
  }

  if (detail.loading) return <p className="text-sm text-ink-500">載入中…</p>
  if (!detail.data) return null
  const { site, usage, fieldLocks, canDelete } = detail.data
  const locked = fieldLocks.filter((f) => f.locked && f.reason)
  const hint = fieldLocks.find((f) => f.field === '_note_ids' && f.reason)

  return (
    <>
      {/* §12.3.1 鎖住的欄位要顯示為什麼鎖住與依據數字 */}
      <Card title={`${site.name}（${site.site_id}）`} subtitle="識別欄位">
        <div className="space-y-1.5 font-mono text-sm">
          <LockRow label="廠區代碼" value={site.site_id} lock={fieldLocks.find((f) => f.field === 'site_id')} />
          <LockRow label="PV 號前綴" value={site.panel_id_prefix} lock={fieldLocks.find((f) => f.field === 'panel_id_prefix')} />
          <LockRow label="料件號前綴" value={site.item_id_prefix} lock={fieldLocks.find((f) => f.field === 'item_id_prefix')} />
          <LockRow label="時區" value={site.timezone} lock={fieldLocks.find((f) => f.field === 'timezone')} />
        </div>
        {hint && (
          <p className="mt-3 text-xs text-emerald-700">{hint.reason}</p>
        )}
        {locked.length === 0 && (
          <p className="mt-3 text-xs text-ink-500">這個廠區還沒有任何紀錄，識別欄位（除代碼外）都還能改。</p>
        )}
      </Card>

      <Card title="靜態資料" subtitle="全部可隨時編輯（純顯示，不參與運算）">
        <KV items={[
          ['地址', site.address ?? '—'],
          ['聯絡人', site.contact_name ?? '—'],
          ['統一編號', site.tax_id ?? '—'],
          ['環保署事業機構代碼', site.epa_facility_no ?? '—'],
          ['廢棄物處理許可證', site.permit_no ?? '—'],
          ['許可證有效期限', site.permit_valid_until ?? '—'],
        ]} />
        {detail.data.permitWarning && (
          <div className="mt-3"><Alert tone="warn" title="許可證提醒">{detail.data.permitWarning}</Alert></div>
        )}
      </Card>

      {/* §12.3.3 設定完成度檢查清單 */}
      {readiness.data && (
        <Card
          title="廠區設定完成度"
          subtitle={`${readiness.data.passed} / ${readiness.data.total}`}
          tone={readiness.data.canActivate ? 'ok' : 'warn'}
        >
          <ul className="space-y-1.5 text-sm">
            {readiness.data.items.map((i) => (
              <li key={i.key} className="flex items-start gap-2">
                <span className={i.ok ? 'text-emerald-600' : 'text-red-600'}>{i.ok ? '✓' : '✗'}</span>
                <span className="w-24 shrink-0 text-ink-700">{i.label}</span>
                <span className="flex-1 text-ink-600">
                  {i.detail}
                  {i.unavailable && (
                    <span className="block text-xs text-amber-700 mt-0.5">{i.unavailable}</span>
                  )}
                </span>
                {!i.ok && i.href && (
                  <Button size="sm" onClick={() => navigate(i.href!)}>前往設定</Button>
                )}
              </li>
            ))}
          </ul>

          {/* §12.3.3 全部滿足才能啟用 */}
          {can('site.write') && site.status !== 'active' && (
            <div className="mt-4">
              <Button
                variant="primary"
                size="lg"
                disabled={!readiness.data.canActivate}
                onClick={() => void act(
                  () => api.post(`/api/admin/sites/${siteId}/status`, { status: 'active' }),
                  `${site.name} 已啟用。`,
                )}
              >啟用廠區</Button>
              {!readiness.data.canActivate && (
                <p className="mt-2 text-xs text-ink-500">
                  7 項全部滿足才能啟用。這是為了擋掉半設定好的廠區——
                  否則操作員登入後會發現什麼都做不了。
                </p>
              )}
            </div>
          )}
        </Card>
      )}

      {/* §12.3.4 停用與刪除 */}
      {can('site.write') && (
        <Card title="狀態" tone={site.status === 'suspended' ? 'warn' : 'plain'}>
          <p className="text-sm text-ink-600 mb-3">
            目前：<strong>{STATUS_LABEL[site.status]}</strong>
            {site.status === 'active' && '（可以作業）'}
            {site.status === 'suspended' && '（不能新增作業，但查詢與報表照常）'}
          </p>
          <div className="flex flex-wrap gap-2">
            {site.status === 'active' && (
              <Button onClick={() => void act(
                () => api.post(`/api/admin/sites/${siteId}/status`, { status: 'suspended' }),
                `${site.name} 已暫停營運。`,
              )}>暫停營運</Button>
            )}
            {site.status === 'suspended' && (
              <Button variant="primary" onClick={() => void act(
                () => api.post(`/api/admin/sites/${siteId}/status`, { status: 'active' }),
                `${site.name} 已恢復營運。`,
              )}>恢復營運</Button>
            )}

            {/* §12.3.4 一旦有第一筆事件，刪除的按鈕就消失 */}
            {canDelete ? (
              <Button
                variant="danger"
                onClick={() => {
                  if (!confirm(`確定要刪除廠區「${site.name}」嗎？\n\n這個廠區還沒有任何作業紀錄，所以可以刪除。刪除後無法復原。`)) return
                  void act(() => api.del(`/api/admin/sites/${siteId}`), `${site.name} 已刪除。`)
                }}
              >刪除廠區</Button>
            ) : (
              <div className="flex-1 min-w-[280px]">
                <Alert tone="warn">
                  此廠區已有 {count(usage.eventCount)} 筆作業紀錄
                  {usage.assignedUsers > 0 && `、${count(usage.assignedUsers)} 個指派帳號`}
                  ，無法刪除。不再使用的廠區請改為「暫停」，歷史資料會完整保留。
                </Alert>
              </div>
            )}
          </div>
        </Card>
      )}
    </>
  )
}

function LockRow({ label, value, lock }: {
  label: string
  value: string
  lock?: { locked: boolean; reason?: string }
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
      <span className="w-24 shrink-0 font-sans text-ink-500">{label}</span>
      <span className="text-ink-900">{value}</span>
      {lock?.locked && (
        <span className="font-sans text-xs text-ink-500">🔒 {lock.reason}</span>
      )}
    </div>
  )
}

/**
 * §12.3.2 建立新廠區的三步精靈。
 * §12.3.6 初始管理者第一次登入、系統裡還沒有廠區時，`/api/auth/me` 的 home 會指到這裡。
 */
function NewSiteWizard({ onDone }: { onDone: (siteId: string) => void }) {
  const sites = useQuery<{ items: Site[] }>('/api/admin/sites')
  const users = useQuery<{ items: { id: number; displayName: string; email: string }[] }>('/api/admin/users')
  const [step, setStep] = useState(0)
  const [form, setForm] = useState({
    siteId: '', name: '', shortName: '',
    panelIdPrefix: '', itemIdPrefix: '', timezone: 'Asia/Taipei',
    address: '', contactName: '', taxId: '', epaFacilityNo: '', permitNo: '', permitValidUntil: '',
  })
  const [source, setSource] = useState<{ kind: 'copy' | 'template' | 'blank'; fromSiteId?: string }>({ kind: 'template' })
  const [supervisors, setSupervisors] = useState<number[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{
    site: Site
    copiedKeys: string[]
    copiedMeasurementPoints: { id: string; name: string; enabled: boolean }[]
    disabledMeasurementPoints: { id: string; name: string; enabled: boolean }[]
  } | null>(null)

  const existing = sites.data?.items ?? []
  const derived = form.siteId
    ? { panel: `PVR-01-${form.siteId}`, item: `IT-${form.siteId}` }
    : { panel: '', item: '' }

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const r = await api.post<typeof result>('/api/admin/sites', {
        siteId: form.siteId.trim(),
        name: form.name.trim(),
        shortName: form.shortName.trim(),
        panelIdPrefix: (form.panelIdPrefix || derived.panel).trim(),
        itemIdPrefix: (form.itemIdPrefix || derived.item).trim(),
        timezone: form.timezone,
        address: form.address || null,
        contactName: form.contactName || null,
        taxId: form.taxId || null,
        epaFacilityNo: form.epaFacilityNo || null,
        permitNo: form.permitNo || null,
        permitValidUntil: form.permitValidUntil || null,
        configSource: source,
        supervisorUserIds: supervisors,
      })
      setResult(r)
      setStep(3)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  if (result) {
    return (
      <Card title={`廠區 ${result.site.name} 已建立`} tone="ok">
        <KV items={[
          ['廠區代碼', <Mono key="a">{result.site.site_id}</Mono>],
          ['PV 號前綴', <Mono key="b">{result.site.panel_id_prefix}</Mono>],
          ['料件號前綴', <Mono key="c">{result.site.item_id_prefix}</Mono>],
          ['狀態', '設定中（完成 7 項檢查後才能啟用）'],
        ]} />

        {/*
          §12.3.2 的關鍵一段：複製設定時量測點的啟用狀態一併複製，
          **必須讓管理者看到他複製了什麼**。
        */}
        {result.disabledMeasurementPoints.length > 0 && (
          <div className="mt-4">
            <Alert tone="warn" title={`已套用 ${result.copiedMeasurementPoints.length} 個量測點，其中 ${result.disabledMeasurementPoints.length} 個為停用狀態`}>
              <ul className="mt-1 space-y-0.5 font-mono text-xs">
                {result.disabledMeasurementPoints.map((m) => (
                  <li key={m.id}>{m.id}　{m.name}　停用</li>
                ))}
              </ul>
              <p className="mt-2">
                新廠區將沿用同樣的量測缺口。若新廠區已有相應的秤，請到作業設定啟用。
              </p>
            </Alert>
          </div>
        )}

        <div className="mt-4">
          <Button variant="primary" onClick={() => onDone(result.site.site_id)}>
            前往這個廠區的設定完成度
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <Card title="建立廠區" subtitle={`步驟 ${step + 1} / 3`}>
      {step === 0 && (
        <div className="space-y-3">
          <Alert tone="warn" title="廠區代碼送出後就不能改">
            代碼是所有事件與投影的外鍵。打錯的話只能在「還沒有任何事件」的狀態下刪除重建
            ——一旦現場開始作業就永久固定了。請再確認一次。
          </Alert>
          <Field label="廠區代碼" hint="2–8 位英數，沿用現行場站代碼（例 2634）" required>
            <TextInput value={form.siteId} autoFocus className="font-mono"
              onChange={(e) => setForm({ ...form, siteId: e.target.value.replace(/[^A-Za-z0-9]/g, '') })} />
          </Field>
          <Field label="廠區名稱" required>
            <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="台南廠" />
          </Field>
          <Field label="簡稱" required>
            <TextInput value={form.shortName} onChange={(e) => setForm({ ...form, shortName: e.target.value })}
              placeholder="台南" />
          </Field>
          <Field label="PV 號前綴" hint={`預設由代碼推導：${derived.panel || '（先填代碼）'}`}>
            <TextInput value={form.panelIdPrefix} className="font-mono" placeholder={derived.panel}
              onChange={(e) => setForm({ ...form, panelIdPrefix: e.target.value })} />
          </Field>
          <Field label="料件號前綴" hint={`預設由代碼推導：${derived.item || '（先填代碼）'}`}>
            <TextInput value={form.itemIdPrefix} className="font-mono" placeholder={derived.item}
              onChange={(e) => setForm({ ...form, itemIdPrefix: e.target.value })} />
          </Field>
          <details className="rounded border border-ink-200 p-3">
            <summary className="cursor-pointer text-sm text-ink-700">靜態資料（全部選填，之後隨時可補）</summary>
            <div className="mt-3 space-y-2">
              <Field label="地址"><TextInput value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
              <Field label="廠區負責人"><TextInput value={form.contactName}
                onChange={(e) => setForm({ ...form, contactName: e.target.value })} /></Field>
              <Field label="統一編號"><TextInput value={form.taxId}
                onChange={(e) => setForm({ ...form, taxId: e.target.value })} /></Field>
              <Field label="環保署事業機構代碼"><TextInput value={form.epaFacilityNo}
                onChange={(e) => setForm({ ...form, epaFacilityNo: e.target.value })} /></Field>
              <Field label="廢棄物處理許可證字號"><TextInput value={form.permitNo}
                onChange={(e) => setForm({ ...form, permitNo: e.target.value })} /></Field>
              <Field label="許可證有效期限" hint="到期前 60 天會在廠區管理畫面提醒">
                <TextInput type="date" value={form.permitValidUntil}
                  onChange={(e) => setForm({ ...form, permitValidUntil: e.target.value })} /></Field>
            </div>
          </details>
          <Button variant="primary" size="lg"
            disabled={!form.siteId || !form.name || !form.shortName}
            onClick={() => setStep(1)}>下一步：初始設定</Button>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-3">
          <p className="text-sm text-ink-600">
            新廠區建立後是一個空殼：沒有站台、沒有量測點、沒有容器、沒有儲位、沒有號碼、沒有人。
            要求從零手動建完不現實，所以先選一個起點。
          </p>
          {([
            ['copy', '複製既有廠區', '從選定廠區複製站台、設備實例、量測點、容器、儲位、秤具、批量規則。適合新廠區與現有廠區產線相同'],
            ['template', '套用預設範本', '載入預設設定（依設備團隊現況，含刻意保留的兩個量測缺口）。適合第一個廠區'],
            ['blank', '空白', '什麼都不建，自己到作業設定慢慢加。適合產線特殊'],
          ] as const).map(([kind, label, desc]) => (
            <label key={kind}
              className={`block rounded-md border p-3 cursor-pointer ${
                source.kind === kind ? 'border-sky-400 bg-sky-50' : 'border-ink-200'}`}>
              <div className="flex items-start gap-2">
                <input type="radio" name="source" className="mt-1" checked={source.kind === kind}
                  disabled={kind === 'copy' && existing.length === 0}
                  onChange={() => setSource(kind === 'copy'
                    ? { kind, fromSiteId: existing[0]?.site_id }
                    : { kind })} />
                <div>
                  <p className="text-sm font-medium text-ink-900">{label}</p>
                  <p className="text-xs text-ink-500">{desc}</p>
                  {kind === 'copy' && existing.length === 0 && (
                    <p className="text-xs text-amber-700 mt-1">系統裡還沒有別的廠區可以複製。</p>
                  )}
                </div>
              </div>
              {kind === 'copy' && source.kind === 'copy' && existing.length > 0 && (
                <div className="mt-2 pl-6">
                  <Select value={source.fromSiteId ?? ''}
                    onChange={(e) => setSource({ kind: 'copy', fromSiteId: e.target.value })}>
                    {existing.map((s) => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
                  </Select>
                </div>
              )}
            </label>
          ))}
          <div className="flex gap-2">
            <Button onClick={() => setStep(0)}>上一步</Button>
            <Button variant="primary" onClick={() => setStep(2)}>下一步：指派人員</Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <p className="text-sm text-ink-600">
            至少指派一位該廠區的主管，否則沒有人能設定這個廠區。
          </p>
          {(users.data?.items ?? []).length === 0
            ? <Alert tone="warn" title="沒有可指派的帳號">請先到帳號管理建立帳號。</Alert>
            : (
              <ul className="space-y-1">
                {users.data!.items.map((u) => (
                  <li key={u.id}>
                    <label className="flex items-center gap-2 rounded border border-ink-200 px-2.5 py-2 text-sm">
                      <input type="checkbox" checked={supervisors.includes(u.id)}
                        onChange={(e) => setSupervisors((s) => e.target.checked
                          ? [...s, u.id] : s.filter((x) => x !== u.id))} />
                      {u.displayName}
                      <span className="text-xs text-ink-400">{u.email}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          {error && <Alert tone="danger">{error}</Alert>}
          <div className="flex gap-2">
            <Button onClick={() => setStep(1)}>上一步</Button>
            <Button variant="primary" size="lg" disabled={supervisors.length === 0 || busy}
              onClick={() => void submit()}>
              {busy ? '建立中…' : '建立廠區'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}
